import ToPayNow from "common/components/subscriptions/ToPayNow";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceEstimateCreation from "common/datahooks/useWorkspaceEstimateCreation";
import useWorkspaceEstimateRenewal from "common/datahooks/useWorkspaceEstimateRenewal";
import useWorkspaceEstimateUpdate from "common/datahooks/useWorkspaceEstimateUpdate";
import { HTTPError } from "common/helpers/HTTP";
import { formatPrice, formatPricingPeriodInfo } from "common/helpers/utils";
import type { Pricing } from "common/types";

export default function PricingDetails({
  pricing,
  accountCounts,
  isUnchangedSubscription,
  isDowngrade,
  alertsComponent,
}: {
  pricing: { addon: Pricing; plan: Pricing };
  accountCounts: number;
  isUnchangedSubscription: boolean;
  isDowngrade: boolean;
  alertsComponent: JSX.Element;
}) {
  const { rental_subscription: currentSubscription } =
    useSelectedWorkspaceContext();
  const { estimateCreation, isLoadingEstimateCreation, estimateCreationError } =
    useWorkspaceEstimateCreation({
      pricing,
      quantity: accountCounts,
      subscription: currentSubscription,
    });
  const { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError } =
    useWorkspaceEstimateUpdate({
      pricing,
      quantity: accountCounts,
      prorate: false,
      subscription: currentSubscription,
    });
  const {
    estimateUpdate: proRatedEstimateUpdate,
    isLoadingEstimateUpdate: isLoadingProratedEstimateUpdate,
    estimateUpdateError: proRatedEstimateUpdateError,
  } = useWorkspaceEstimateUpdate({
    pricing,
    quantity: accountCounts,
    prorate: true,
    subscription: currentSubscription,
  });
  const { estimateRenewal, isLoadingEstimateRenewal, estimateRenewalError } =
    useWorkspaceEstimateRenewal(currentSubscription);

  const error =
    estimateCreationError ||
    estimateUpdateError ||
    estimateRenewalError ||
    proRatedEstimateUpdateError;

  const shouldShowCurrentInfo =
    currentSubscription &&
    currentSubscription.status !== "cancelled" &&
    currentSubscription.status !== "non_renewing";

  const estimate = estimateCreation ?? estimateUpdate;

  const isLoadingEstimate =
    isLoadingEstimateCreation || isLoadingEstimateUpdate;

  return (
    <>
      {shouldShowCurrentInfo && (
        <>
          <div className="flex items-center justify-between px-2" key={3}>
            <span className="text-button-sm text-black-600">
              Current price per account
            </span>
            {isLoadingEstimateRenewal || !estimateRenewal ? (
              <Skeleton className="h-5 w-28" />
            ) : (
              <span className="text-md-medium text-black-500">
                {formatPrice(estimateRenewal.pricePerItem / 100)}/
                {formatPricingPeriodInfo(currentSubscription.item_price)}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between px-2" key={4}>
            <span className="text-button-sm text-black-600">
              Current no. of accounts
            </span>
            <span className="text-md-medium text-black-500">
              {currentSubscription.rented_count}
            </span>
          </div>
          <div className="flex items-center justify-between px-2" key={5}>
            <span className="text-button-sm text-black-600">Current price</span>
            {isLoadingEstimateRenewal || !estimateRenewal ? (
              <Skeleton className="h-5 w-28" />
            ) : (
              <span className="text-md-medium text-black-500">
                {formatPrice(estimateRenewal.amountDue / 100)}/
                {formatPricingPeriodInfo(currentSubscription.item_price)}
              </span>
            )}
          </div>
          <div className="w-full h-px bg-black-200" key={6} />
        </>
      )}
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">
          New price per account
        </span>
        {isLoadingEstimate || !estimate ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-black-950 text-md-bold">
            {estimate
              ? `${formatPrice(estimate.pricePerItem / 100)}/${formatPricingPeriodInfo(pricing.plan)}`
              : "-"}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">
          New no. of accounts
        </span>
        <span className="text-black-950 text-md-bold">{accountCounts}</span>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">New price</span>
        {isLoadingEstimate || !estimate ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-black-950 text-md-bold">
            {estimate
              ? `${formatPrice(estimate.amountDue / 100)}/${formatPricingPeriodInfo(pricing.plan)}`
              : "-"}
          </span>
        )}
      </div>
      {alertsComponent}
      <ToPayNow
        estimate={estimateCreation ? estimateCreation : proRatedEstimateUpdate}
        isLoading={isLoadingEstimateCreation || isLoadingProratedEstimateUpdate}
        isDowngradeOrEqual={isDowngrade || isUnchangedSubscription}
        isInTrial={false}
      />
      {error && (
        <span className="text-red-500 text-xs">
          {error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Error loading estimate, please try again later."}
        </span>
      )}
    </>
  );
}
